import React, { useState } from 'react';
import { MessageSquareWarning, X } from 'lucide-react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { submitFeedback } from '../lib/orbitApi';
import { validateFeedbackDraft } from '../lib/socialUtils';

// Global "report a bug / feedback" entry point, mounted once in
// PhoneFrame so it shows on every page regardless of auth state.
// Unauthenticated submit straight to the backend, which mails it to
// bot@de1ta.space for the operator to see (summarized/forwarded from
// there, outside this app).
export default function FeedbackButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [sending, setSending] = useState(false);

  const close = () => {
    if (sending) return;
    setOpen(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;
    let draft;
    try {
      draft = validateFeedbackDraft({ message, contactEmail });
    } catch (err) {
      toast(err.message);
      return;
    }
    setSending(true);
    try {
      await submitFeedback({ ...draft, page: location.pathname });
      toast('Thanks — feedback sent.');
      setMessage('');
      setContactEmail('');
      setOpen(false);
    } catch (err) {
      toast(err.message || 'Could not send feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="press orbit-icon-button"
        aria-label="Report a bug or send feedback"
        title="Report a bug / feedback"
        style={{
          background: 'rgba(23,17,40,.9)',
          border: '1px solid rgba(160,140,230,.4)',
          boxShadow: '0 4px 18px rgba(0,0,0,.45)',
        }}
      >
        <MessageSquareWarning size={20} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(5,5,12,.72)' }}
          onClick={close}
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm orbit-surface-card"
            style={{ maxHeight: '85vh', overflowY: 'auto' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold" style={{ fontSize: 18, color: '#efeaff' }}>
                Report a bug / feedback
              </h2>
              <button type="button" onClick={close} className="press orbit-icon-button" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={5000}
              rows={5}
              placeholder="What went wrong, or what would you like to see?"
              className="w-full p-3 rounded-2xl outline-none resize-y mb-3"
              style={{ background: 'rgba(255,255,255,.06)', color: '#efeaff', border: '1px solid var(--orbit-border)' }}
              autoFocus
            />

            <label className="block mb-4">
              <span className="block text-xs font-semibold mb-1" style={{ color: '#8079a6' }}>
                Contact email (optional, so we can follow up)
              </span>
              <input
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                type="email"
                placeholder="you@example.com"
                className="orbit-field w-full"
                style={{ background: 'rgba(255,255,255,.06)', color: '#efeaff' }}
              />
            </label>

            <button type="submit" disabled={sending || !message.trim()} className="press orbit-primary-button w-full">
              {sending ? 'Sending…' : 'Send feedback'}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
